import React, { useContext, useRef, useState } from "react";
import {
  Button as AntdButton,
  Col,
  Row,
  ButtonProps as AntdButtonProps,
  message,
  Select,
  Radio,
  Spin,
} from "antd";
import { IoIosAdd } from "react-icons/io";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { useForm, FormProvider, Controller as RHFController } from "react-hook-form";
import Controller from "../../../form-components/controller";
import Input from "../../../form-components/input";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import WmsStorablePicker, { IStorableSelection } from "../../../wms/wms-storable-picker";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

interface IFormValues {
  storeId: number | null;
  mode: "existing" | "new";
  // existing batch
  batchId: number | null;
  addedQuantity: number;
  // new batch
  enteredQuantity: number;
  batchNumber: string;
  expirationDate: string;
  notes: string;
  storableType?: IStorableSelection["storableType"];
  storableId?: IStorableSelection["storableId"];
}

const AddQuantityButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);
  const storableRef = useRef<IStorableSelection | undefined>(undefined);

  const [showFormModal, hideFormModal] = useModal(() => {
    const methods = useForm<IFormValues>({
      defaultValues: {
        storeId: null,
        mode: "new",
        batchId: null,
        addedQuantity: 1,
        enteredQuantity: 1,
        batchNumber: "",
        expirationDate: "",
        notes: "",
      },
    });

    const { control, watch, setValue } = methods as any;
    const [submitting, setSubmitting] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [existingBatches, setExistingBatches] = useState<IBatch[]>([]);
    const setStorable = (val: IStorableSelection | undefined) => {
      storableRef.current = val;
      setValue("storableType", val?.storableType ?? undefined, { shouldValidate: true });
      setValue("storableId", val?.storableId ?? undefined, { shouldValidate: true });
    };

    const selectedStoreId = watch("storeId");
    const mode = watch("mode");

    const handleStoreChange = async (storeId: number) => {
      setValue("storeId", storeId);
      setValue("batchId", null);
      setExistingBatches([]);
      storableRef.current = undefined;
      setValue("storableType", undefined);
      setValue("storableId", undefined);
      if (storeId) {
        setLoadingBatches(true);
        try {
          const { data } = await EndPoints.wmsBatch.getAllBatches({
            productId: passedId,
            storeId,
            perPage: -1,
          });
          setExistingBatches(data?.data ?? []);
        } catch {
          // silent
        } finally {
          setLoadingBatches(false);
        }
      }
    };

    const handleConfirm = async () => {
      const isValid = await methods.trigger();
      if (!isValid) return;

      const data = methods.getValues();

      if (!data.storeId) {
        message.error(t("please_select_store"));
        return;
      }

      setSubmitting(true);
      try {
        if (data.mode === "existing") {
          if (!data.batchId) {
            message.error(t("please_select_batch"));
            setSubmitting(false);
            return;
          }
          const qty = Number(data.addedQuantity);
          if (!qty || qty < 1) {
            message.error(t("must_be_zero_or_positive"));
            setSubmitting(false);
            return;
          }
          const batch = existingBatches.find((b) => b.id === data.batchId);
          if (batch) {
            await EndPoints.wmsBatch.updateBatch(data.batchId, {
              enteredQuantity: batch.enteredQuantity + qty,
            });
          }
        } else {
          if (!data.storableType || !data.storableId) {
            message.error(t("please_select_storage_location"));
            setSubmitting(false);
            return;
          }
          const qty = Number(data.enteredQuantity);
          if (!qty || qty < 1) {
            message.error(t("must_be_zero_or_positive"));
            setSubmitting(false);
            return;
          }
          await EndPoints.wmsBatch.createBatch({
            productId: passedId,
            storeId: data.storeId,
            enteredQuantity: qty,
            batchNumber: data.batchNumber || undefined,
            expirationDate: data.expirationDate || undefined,
            notes: data.notes || undefined,
            storableType: data.storableType,
            storableId: data.storableId,
          });
        }
        actions.getData();
        hideFormModal();
      } catch {
        // error handled by http client
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <FormHookModal
        id="add-quantity-modal"
        confirmButtonText={t("add")}
        loading={submitting || loading.includes("addQuantity")}
        title={t("add_quantity")}
        hideModal={hideFormModal}
        width="50vw"
        style={{ marginTop: "1.6rem", maxHeight: "70vh", overflowY: "auto" }}
        onSubmit={() => {}}
        confirmClick={handleConfirm}
      >
        <FormProvider {...methods}>
          <Row gutter={[16, 12]}>
            {/* Store selector */}
            <Col span={24}>
              <FormItem label={t("store")}>
                <RHFController
                  name="storeId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      style={{ width: "100%" }}
                      value={field.value}
                      onChange={(val) => handleStoreChange(val)}
                      options={quantities.map((q) => ({
                        value: q.store_id,
                        label: getStoreName(q.store_name),
                      }))}
                    />
                  )}
                />
              </FormItem>
            </Col>

            {/* Mode toggle */}
            {selectedStoreId && (
              <Col span={24}>
                <RHFController
                  name="mode"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue("batchId", null);
                        storableRef.current = undefined;
                        setValue("storableType", undefined);
                        setValue("storableId", undefined);
                      }}
                    >
                      <Radio value="new">{t("new_batch")}</Radio>
                      <Radio value="existing">{t("existing_batch")}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            )}

            {/* Existing batch mode */}
            {selectedStoreId && mode === "existing" && (
              <>
                <Col span={24}>
                  <FormItem label={t("select_batch")}>
                    {loadingBatches ? (
                      <Spin size="small" />
                    ) : (
                      <RHFController
                        name="batchId"
                        control={control}
                        rules={{ required: mode === "existing" }}
                        render={({ field }) => (
                          <Select
                            style={{ width: "100%" }}
                            value={field.value}
                            onChange={field.onChange}
                            options={existingBatches.map((b) => ({
                              value: b.id,
                              label: b.expirationDate
                                ? `${b.batchNumber ?? "#" + b.id} — ${b.expirationDate} (${t("available")}: ${b.availableQuantity})`
                                : `${b.batchNumber ?? "#" + b.id} — (${t("batch_has_no_expiration_date")} — (${t("available")}: ${b.availableQuantity})`,
                            }))}
                          />
                        )}
                      />
                    )}
                  </FormItem>
                </Col>
                <Col span={24}>
                  <FormItem label={t("added_quantity")}>
                    <Controller
                      name="addedQuantity"
                      control={control}
                      rules={{
                        validate: (v) =>
                          Number(v) >= 1 || t("must_be_zero_or_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
              </>
            )}

            {/* New batch mode */}
            {selectedStoreId && mode === "new" && (
              <>
                <Col span={12}>
                  <FormItem label={t("entered_quantity")}>
                    <Controller
                      name="enteredQuantity"
                      control={control}
                      rules={{
                        validate: (v) =>
                          Number(v) >= 1 || t("must_be_zero_or_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label={t("expiration_date")}>
                    <Controller
                      name="expirationDate"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="date"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem label={t("note")}>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <div style={{ marginBottom: 6, fontWeight: 500 }}>
                    {t("storage_location")}
                  </div>
                  <WmsStorablePicker
                    storeId={selectedStoreId}
                    onStorableSelected={setStorable}
                  />
                </Col>
              </>
            )}
          </Row>
        </FormProvider>
      </FormHookModal>
    );
  }, [passedId, quantities, loading]);

  return (
    <AntdButton
      type="primary"
      icon={<IoIosAdd />}
      onClick={() => { storableRef.current = undefined; showFormModal(); }}
      {...props}
    />
  );
};

export default AddQuantityButton;
